import { useCallback, useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery } from 'react-query';
import { Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, Typography } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';

import { deleteEventDocumentByReference, getEventDocumentByReference, uploadEventDocument } from 'core/API/events';
import { eventsService, getEventDocumentTemplatePdfUrl } from 'core/services/eventsService';
import {
	buildUploadedEventDocumentUrl,
	encodeRemoteFileUrl,
	isApiResourceReference,
} from 'core/utils/eventDocumentUrls';
import { EventDocumentPdfEditorModal } from 'module/settings/settingsGameMasterTabs/EventsSetting/EventDocumentPdfEditorModal';
import {
	eventsSectionAddButtonIconSx,
	eventsSectionAddButtonLabelSx,
	eventsSectionAddButtonSx,
} from 'module/events/constants/sectionAddButtonSx';
import { ReadOnlySelectAutocomplete } from 'module/events/components/ReadOnlySelectAutocomplete';

interface BanquetEventOrderTemplateFillProps {
	setFieldValue: (field: string, value: any) => void;
	value: string;
	workplaceId: number;
	labelClassName?: string;
	onRefRemoved?: (ref: string) => void;
}

interface PreviewItem {
	ref: string;
	url: string;
	label: string;
	templateIri?: string;
}

interface PendingRow {
	id: string;
	selectedId: string;
}

export default function BanquetEventOrderTemplateFill({
	setFieldValue,
	value,
	workplaceId,
	labelClassName,
	onRefRemoved,
}: BanquetEventOrderTemplateFillProps): JSX.Element {
	const [pendingRows, setPendingRows] = useState<PendingRow[]>([]);
	const [editorOpen, setEditorOpen] = useState(false);
	const [pdfUrl, setPdfUrl] = useState<string | null>(null);
	const [editorTitle, setEditorTitle] = useState('');
	const [editingRef, setEditingRef] = useState<string | null>(null);
	const [editingPendingRowId, setEditingPendingRowId] = useState<string | null>(null);
	const [editingTemplateIri, setEditingTemplateIri] = useState<string | undefined>(undefined);
	const [deleteConfirmRef, setDeleteConfirmRef] = useState<string | null>(null);
	const [previewLoading, setPreviewLoading] = useState(false);
	const [previewItems, setPreviewItems] = useState<PreviewItem[]>([]);
	const [isUploading, setIsUploading] = useState(false);

	const { data: templates = [], isLoading } = useQuery(
		['event-document-templates', workplaceId],
		() => eventsService.getEventDocumentTemplates({ workplace: workplaceId }),
		{
			staleTime: 60_000,
			enabled: Number.isFinite(workplaceId) && workplaceId > 0,
		}
	);

	const activeTemplates = useMemo(
		() => templates.filter((t) => t.active !== false).sort((a, b) => a.sortOrder - b.sortOrder),
		[templates]
	);

	const { mutateAsync: uploadFile } = useMutation(uploadEventDocument);
	const { mutateAsync: deleteUploadedFile } = useMutation(deleteEventDocumentByReference);

	const addPendingRow = () => {
		setPendingRows((prev) => [...prev, { id: String(Date.now()), selectedId: '' }]);
	};

	const removePendingRow = (id: string) => {
		setPendingRows((prev) => prev.filter((r) => r.id !== id));
	};

	const updatePendingRowTemplate = (id: string, selectedId: string) => {
		setPendingRows((prev) => prev.map((r) => (r.id === id ? { ...r, selectedId } : r)));
	};

	const openEditorForPendingRow = (rowId: string, templateId: string) => {
		const template = activeTemplates.find((t) => String(t.id) === templateId);
		if (!template?.filePath) return;
		const url = getEventDocumentTemplatePdfUrl(template.filePath);
		if (!url) return;
		setEditorTitle(template.name);
		setPdfUrl(url);
		setEditingRef(null);
		setEditingPendingRowId(rowId);
		setEditorOpen(true);
	};

	const openEditorForEdit = (item: PreviewItem) => {
		if (!item.url) return;
		setEditorTitle(item.label);
		setPdfUrl(item.url);
		setEditingRef(item.ref);
		setEditingPendingRowId(null);
		setEditingTemplateIri(item.templateIri);
		setEditorOpen(true);
	};

	const closeEditor = useCallback(() => {
		setEditorOpen(false);
		setPdfUrl(null);
		setEditingRef(null);
		setEditingPendingRowId(null);
		setEditingTemplateIri(undefined);
	}, []);

	const handleSaveFilledPdf = async (file: File) => {
		setIsUploading(true);
		try {
			let templateIri: string | undefined;
			if (editingPendingRowId) {
				const row = pendingRows.find((r) => r.id === editingPendingRowId);
				const template = activeTemplates.find((t) => String(t.id) === row?.selectedId);
				if (template?.id && workplaceId) {
					templateIri = `/api/event_document_templates/${template.id}`;
				}
			} else if (editingRef) {
				templateIri = editingTemplateIri;
			}

			const data = await uploadFile({
				file,
				template: templateIri,
				workplace: `/api/workplaces/${workplaceId}`,
			});

			const docValue = data?.['@id'] || data?.path || data?.filePath || data?.file_path || '';
			if (!docValue) return;

			const existing = (value || '').split(',').map((v) => v.trim()).filter(Boolean);

			if (editingRef) {
				if (editingRef.startsWith('/api/event_documents/') || editingRef.startsWith('/api/event-documents/')) {
					try { await deleteUploadedFile(editingRef); } catch { /* best-effort */ }
				}
				const next = existing.map((v) => (v === editingRef ? docValue : v));
				setFieldValue('banquet_event_order', next.join(','));
			} else {
				if (!existing.includes(docValue)) {
					setFieldValue('banquet_event_order', [...existing, docValue].join(','));
				}
				if (editingPendingRowId) {
					removePendingRow(editingPendingRowId);
				}
			}
		} finally {
			setIsUploading(false);
		}
	};

	useEffect(() => {
		let cancelled = false;
		const refs = (value || '').split(',').map((v) => v.trim()).filter(Boolean);

		const resolveOne = async (ref: string): Promise<PreviewItem> => {
			if (isApiResourceReference(ref)) {
				try {
					const doc = await getEventDocumentByReference(ref);
					const raw = doc.filePath || doc.file_path || doc.path || '';
					if (!raw) return { ref, url: '', label: 'Preview unavailable' };
					const url = buildUploadedEventDocumentUrl(raw);
					const label = doc.fileName || doc.file_name || String(raw).split('/').pop() || 'Open PDF';
					const templateIri = doc.template || undefined;
					return { ref, url, label, templateIri };
				} catch {
					return { ref, url: '', label: 'Preview unavailable' };
				}
			}
			if (/^https?:\/\//i.test(ref)) {
				return { ref, url: encodeRemoteFileUrl(ref), label: ref.split('/').pop() || 'Open PDF' };
			}
			const url = buildUploadedEventDocumentUrl(ref);
			const clean = ref.replace(/^\/+/, '').replace(/^uploads\//, '');
			return { ref, url, label: clean.split('/').pop() || 'Open PDF' };
		};

		const resolveAll = async () => {
			if (!refs.length) {
				if (!cancelled) setPreviewItems([]);
				return;
			}
			setPreviewLoading(true);
			const items = await Promise.all(refs.map((ref) => resolveOne(ref)));
			if (!cancelled) {
				setPreviewItems(items);
				setPreviewLoading(false);
			}
		};

		resolveAll();
		return () => { cancelled = true; };
	}, [value]);

	const handleDeleteRef = (ref: string) => {
		const next = (value || '').split(',').map((v) => v.trim()).filter(Boolean).filter((v) => v !== ref);
		setFieldValue('banquet_event_order', next.join(','));
		onRefRemoved?.(ref);
	};

	return (
		<Box>
			<label className={labelClassName}>Banquet Event Order</label>

			<Button
				type="button"
				fullWidth
				onClick={addPendingRow}
				disabled={isLoading || activeTemplates.length === 0}
				sx={eventsSectionAddButtonSx}
			>
				<Box component="span" sx={eventsSectionAddButtonLabelSx}>
					{isLoading ? (
						<CircularProgress size={14} color="inherit" />
					) : (
						<AddIcon sx={eventsSectionAddButtonIconSx} />
					)}
					Add Banquet Event Order
				</Box>
			</Button>

			{!isLoading && activeTemplates.length === 0 && (
				<Typography variant="body2" sx={{ color: '#666', fontSize: 12, mb: 2, lineHeight: 1.4 }}>
					No Banquet PDF templates found. Add them under Setting → Events → Banquet event order.
				</Typography>
			)}

			<Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
			{previewLoading ? (
				<Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 1 }}>
					<CircularProgress size={16} />
					<Typography variant="body2" sx={{ color: '#5a6b82', fontSize: 13 }}>Loading preview...</Typography>
				</Box>
			) : (
				previewItems.map((item) => (
					<Box
						key={item.ref}
						sx={{
							display: 'flex',
							alignItems: 'center',
							justifyContent: 'space-between',
							px: 1.25,
							py: 1,
							border: '1px solid #dbe3ee',
							borderRadius: '6px',
							background: '#f9fbff',
							gap: 1,
						}}
					>
						{item.url ? (
							<a
								href={item.url}
								target="_blank"
								rel="noopener noreferrer"
								style={{
									color: '#2f5f99',
									textDecoration: 'underline',
									fontSize: 13,
									flex: 1,
									minWidth: 0,
									whiteSpace: 'nowrap',
									overflow: 'hidden',
									textOverflow: 'ellipsis',
								}}
							>
								{item.label}
							</a>
						) : (
							<Typography sx={{ color: '#5a6b82', fontSize: 13, flex: 1 }}>{item.label}</Typography>
						)}
						<Box sx={{ display: 'flex', gap: 0.5, flexShrink: 0 }}>
							<IconButton
								type="button"
								size="small"
								title="Edit PDF"
								disabled={!item.url || isUploading}
								onClick={() => openEditorForEdit(item)}
								sx={{ color: '#1976D2' }}
							>
								<EditIcon fontSize="small" />
							</IconButton>
							<IconButton
								type="button"
								size="small"
								color="error"
								title="Remove"
								onClick={() => setDeleteConfirmRef(item.ref)}
								aria-label="Remove attached PDF"
							>
								<DeleteIcon fontSize="small" />
							</IconButton>
						</Box>
					</Box>
				))
			)}

			{pendingRows.map((row) => {
				const selectedTemplate = activeTemplates.find((t) => String(t.id) === row.selectedId);
				return (
					<Box key={row.id} sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
						<Box sx={{ flex: 1 }}>
							<ReadOnlySelectAutocomplete
								options={activeTemplates}
								value={selectedTemplate ?? null}
								onChange={(template) =>
									updatePendingRowTemplate(row.id, template ? String(template.id) : '')
								}
								getOptionLabel={(template) => template.name}
								isOptionEqualToValue={(option, val) => option.id === val.id}
								label="PDF Template"
								placeholder="Choose PDF Template"
								error={!row.selectedId}
								textFieldSx={{ '& .MuiOutlinedInput-root': { backgroundColor: '#fff' } }}
							/>
						</Box>
						<Button
							type="button"
							variant="outlined"
							size="small"
							disabled={!selectedTemplate || isUploading}
							onClick={() => openEditorForPendingRow(row.id, row.selectedId)}
							style={{ color: '#1976D2', border: '1px solid #1976D2', whiteSpace: 'nowrap' }}
						>
							{isUploading && editingPendingRowId === row.id ? 'Uploading…' : 'Fill & Attach PDF'}
						</Button>
						<IconButton
							type="button"
							onClick={() => removePendingRow(row.id)}
							color="error"
							size="small"
							title="Remove"
							sx={{ mt: 1 }}
						>
							<DeleteIcon />
						</IconButton>
					</Box>
				);
			})}
			</Box>

			<EventDocumentPdfEditorModal
				open={editorOpen}
				onClose={closeEditor}
				pdfUrl={pdfUrl}
				documentTitle={editorTitle}
				onSave={handleSaveFilledPdf}
			/>

			<Dialog open={deleteConfirmRef !== null} onClose={() => setDeleteConfirmRef(null)}>
				<DialogTitle>Delete Attached PDF</DialogTitle>
				<DialogContent>
					<Typography>Are you sure you want to delete this attached PDF? This action cannot be undone.</Typography>
				</DialogContent>
				<DialogActions>
					<Button
						onClick={() => setDeleteConfirmRef(null)}
						style={{ color: '#1976D2', border: '1px solid #1976D2' }}
						variant="outlined"
					>
						Cancel
					</Button>
					<Button
						variant="contained"
						style={{ backgroundColor: '#1976D2', color: '#fff' }}
						onClick={() => {
							if (deleteConfirmRef) void handleDeleteRef(deleteConfirmRef);
							setDeleteConfirmRef(null);
						}}
					>
						Delete
					</Button>
				</DialogActions>
			</Dialog>
		</Box>
	);
}
